import { useMutation, useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { GroupType, ReasonToken, Recipient } from '@bonusly/recipient-entities';

import { IncentiveData, Incentives } from '../types/announcements';

export enum QueryKey {
	Incentives = 'incentives',
	IncentiveData = 'incentive-data',
	UserProfile = 'recognition-user',
}

interface GetIncentivesResponse {
	result: Incentives;
	success: boolean;
}

interface GetIncentiveDataResponse {
	result: IncentiveData;
	success: boolean;
}

interface CreateApprovalRequestParams {
	award_id: string;
	claim_reason: string;
	giver_id: string;
}

interface CreateApprovalRequestResponse {
	data: {
		success: boolean;
	};
}

export const useGetIncentives = ({ recipients }: { recipients?: Recipient[] }) => {
	const http = useHttpClient('v2');

	const recipientIds = recipients?.map(({ id }) => id);

	return useQuery(
		[QueryKey.Incentives, recipientIds],
		() => {
			// NOTE: Turn recipient into reason token
			const reason_tokens: ReasonToken[] = recipients
				? recipients.map(({ id, dimension, displayName, username, userIds }) => ({
						context: 'recipient' as const,
						display_name: displayName,
						id: id,
						token: username,
						type: dimension as GroupType,
						user_ids: userIds,
					}))
				: [];

			return http.post<GetIncentivesResponse>('/incentives/announcement_incentives', {
				params: {
					reason_tokens: JSON.stringify(reason_tokens),
				},
			});
		},
		{
			retry: false,
			refetchOnWindowFocus: false,
			select: (data) => data.data.result.items,
		}
	);
};

export const useGetIncentiveDetails = (incentiveId: string) => {
	const http = useHttpClient('v2');

	return useQuery(
		[QueryKey.IncentiveData, incentiveId],
		() =>
			http.get<GetIncentiveDataResponse>(`/awards/${incentiveId}`, {
				params: { view: 'claim_modal' },
			}),
		{
			retry: false,
			select: (data) => {
				return data.data.result;
			},
		}
	);
};

export const useCreateApprovalRequest = (options = {}) => {
	const http = useHttpClient('v2');
	return useMutation(
		(params: CreateApprovalRequestParams) => {
			return http.post<CreateApprovalRequestResponse>('/approval_requests', {
				...params,
			});
		},
		{
			retry: false,
			...options,
		}
	);
};
